import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import authService, { User } from '../services/auth';
import projectService from '../services/project';
import api from '../services/api';

interface DashboardStats {
  total_projects: number;
  active_projects: number;
  completed_projects: number;
  total_seedlings_planted: number;
  total_area_hectares: string;
  total_samples: number;
  total_monitoring_sessions: number;
  total_users: number;
  total_partners: number;
}

const Dashboard: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadDashboardData = async () => {
      // Check if user is authenticated
      if (!authService.isAuthenticated()) {
        navigate('/login');
        return;
      }

      const userType = authService.getUserType();
      
      if (userType === 'partner') {
        // Redirect partners to partner dashboard
        navigate('/partner/dashboard');
        return;
      }

      try {
        setLoading(true);
        
        // Get user data
        const storedUser = authService.getStoredUser();
        if (storedUser && 'name' in storedUser) {
          setUser(storedUser as User);
        }

        // Fetch dashboard stats
        const [projectStats, usersResponse, partnersResponse] = await Promise.all([
          projectService.getProjectStats(),
          api.get('/users').catch(() => ({ data: { data: [] } })),
          api.get('/partners').catch(() => ({ data: { data: { data: [] } } }))
        ]);

        // Total sampel & sesi monitoring diambil dari endpoint statistik (backend
        // menghitung dari seluruh tabel), bukan dijumlahkan dari satu halaman
        // hasil paginasi.
        const totalSamples = projectStats.total_samples || 0;
        const totalMonitoringSessions = projectStats.total_monitoring_sessions || 0;

        setStats({
          total_projects: projectStats.total_projects || 0,
          active_projects: projectStats.active_projects || 0,
          completed_projects: projectStats.completed_projects || 0,
          total_seedlings_planted: projectStats.total_seedlings_planted || 0,
          total_area_hectares: projectStats.total_area_hectares || '0',
          total_samples: totalSamples,
          total_monitoring_sessions: totalMonitoringSessions,
          // Paginator menyediakan field `total`; panjang array hanya isi satu halaman.
          total_users: usersResponse.data?.data?.total ?? 0,
          total_partners: partnersResponse.data?.data?.total ?? 0
        });
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [navigate]);

  // const handleLogout = async () => {
  //   await authService.logout();
  //   navigate('/login');
  // };

  // Memoize calculated values
  // const totalUsersAndPartners = useMemo(() => {
  //   return (stats?.total_users || 0) + (stats?.total_partners || 0);
  // }, [stats?.total_users, stats?.total_partners]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mangrove-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400">Selamat datang di Dashboard Seasoldier, {user?.name}</p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow dark:shadow-gray-900/50 rounded-lg transition-colors">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-mangrove-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">P</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Proyek</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">{stats?.total_projects || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow dark:shadow-gray-900/50 rounded-lg transition-colors">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 dark:bg-blue-600 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">S</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Sampel</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">{stats?.total_samples || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow dark:shadow-gray-900/50 rounded-lg transition-colors">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 dark:bg-green-600 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">M</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Sesi Monitoring</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">{stats?.total_monitoring_sessions || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow dark:shadow-gray-900/50 rounded-lg transition-colors">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 dark:bg-purple-600 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">👥</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Users & Partners</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">{(stats?.total_users || 0) + (stats?.total_partners || 0)}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg transition-colors">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
            Selamat Datang di Dashboard Seasoldier
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Platform untuk transparansi data monitoring mangrove Seasoldier. 
            Kelola proyek penanaman mangrove, catat data sampel, dan pantau perkembangannya dari waktu ke waktu.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {authService.hasPermission('projects.create') && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-colors">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Mulai Proyek Baru</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Buat proyek monitoring mangrove baru dengan data lengkap lokasi dan bibit.
                </p>
                <Link
                  to="/projects/create"
                  className="inline-block bg-mangrove-600 hover:bg-mangrove-700 dark:bg-mangrove-700 dark:hover:bg-mangrove-800 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Tambah Proyek
                </Link>
              </div>
            )}
            
            {authService.hasPermission('users.read') && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-colors">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Kelola Pengguna</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Tambah pengguna baru dan atur peran akses untuk tim monitoring.
                </p>
                <Link 
                  to="/users"
                  className="inline-block bg-ocean-600 hover:bg-ocean-700 dark:bg-ocean-700 dark:hover:bg-ocean-800 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Kelola Users
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;